import React, { useCallback, useEffect, useRef, useState } from "react";
import { TicketMessage, UploadUrlFile, UploadUrlsResponse, ConfirmAttachmentItem } from "../../services/supportTicket.service";
import { MessageAttachments, ReplyComposer } from "./ReplyComposer";

export const ChatIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

/** Small red count pill used on the chat button, nav items and tabs. */
export const CountBadge: React.FC<{ count: number; className?: string }> = ({ count, className = "" }) =>
  count > 0 ? (
    <span className={`inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-none text-white ${className}`}>
      {count > 9 ? "9+" : count}
    </span>
  ) : null;

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

interface Props {
  title: string;
  subtitle?: string;
  /** Which sender_role is "me" (right-aligned, solid bubble). */
  ownRole: "clinic" | "admin";
  /** Label for the other party's bubbles. */
  otherLabel: string;
  messages: TicketMessage[];
  placeholder: string;
  requestUploadUrls: (files: UploadUrlFile[]) => Promise<UploadUrlsResponse>;
  onSend: (body: string, attachments: ConfirmAttachmentItem[]) => Promise<TicketMessage>;
  onSent: (msg: TicketMessage) => void;
  onClose?: () => void;
  /** Unsend one of my own messages. Omit to hide the control. */
  onUnsend?: (messageId: string) => void | Promise<void>;
  /** Pinned bottom-right chat window instead of an inline card. */
  floating?: boolean;
}

const SIZE_KEY = "kaagazy_support_chat_size";
const MIN_W = 320, MIN_H = 380;

function readSize(): { w: number; h: number } {
  try {
    const raw = localStorage.getItem(SIZE_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      if (typeof p.w === "number" && typeof p.h === "number") return p;
    }
  } catch { /* storage unavailable */ }
  return { w: 352, h: 520 };
}

/**
 * Side chat "square": light-blue card, separate from the ticket details.
 * Shows only the conversation (replies) — the original report and its
 * attachments live in the ticket details next to it.
 */
export const ChatPanel: React.FC<Props> = ({
  title, subtitle, ownRole, otherLabel, messages, placeholder, requestUploadUrls, onSend, onSent, onClose, floating = false, onUnsend,
}) => {
  const endRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState(readSize);
  const sizeRef = useRef(size);
  sizeRef.current = size;

  // Drag the left / top / top-left edge to resize; the window stays pinned bottom-right.
  const startResize = useCallback((e: React.PointerEvent, axis: "x" | "y" | "xy") => {
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const { w: w0, h: h0 } = sizeRef.current;
    const onMove = (ev: PointerEvent) => {
      const maxW = Math.min(900, window.innerWidth - 32);
      const maxH = window.innerHeight - 32;
      setSize({
        w: axis === "y" ? w0 : Math.max(MIN_W, Math.min(maxW, w0 + (startX - ev.clientX))),
        h: axis === "x" ? h0 : Math.max(MIN_H, Math.min(maxH, h0 + (startY - ev.clientY))),
      });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      try { localStorage.setItem(SIZE_KEY, JSON.stringify(sizeRef.current)); } catch { /* ignore */ }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [messages.length]);

  return (
    <section
      className={`flex flex-col rounded-2xl border-2 border-sky-300 bg-sky-50 overflow-hidden ${
        floating ? "fixed bottom-4 right-4 z-50 max-w-[calc(100vw-2rem)] shadow-2xl" : "shadow-md"
      }`}
      style={floating ? { width: size.w, height: size.h, maxHeight: "calc(100vh - 2rem)" } : undefined}
      aria-label="Support chat"
    >
      {floating && (
        <>
          <div onPointerDown={e => startResize(e, "x")} className="absolute left-0 top-3 bottom-3 z-10 w-1.5 cursor-ew-resize hover:bg-sky-400/50" title="Drag to resize" />
          <div onPointerDown={e => startResize(e, "y")} className="absolute left-3 right-3 top-0 z-10 h-1.5 cursor-ns-resize hover:bg-sky-400/50" title="Drag to resize" />
          <div onPointerDown={e => startResize(e, "xy")} className="absolute left-0 top-0 z-20 h-4 w-4 cursor-nwse-resize hover:bg-sky-400/50" title="Drag to resize" />
        </>
      )}
      <header className="flex items-center justify-between gap-2 bg-sky-200/70 border-b-2 border-sky-300 px-4 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-sky-600 text-white"><ChatIcon /></span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-sky-900">{title}</p>
            {subtitle && <p className="truncate text-[11px] text-sky-700">{subtitle}</p>}
          </div>
        </div>
        {onClose && (
          <button type="button" onClick={onClose} className="flex-shrink-0 text-sky-700 hover:text-sky-900" aria-label="Close chat">✕</button>
        )}
      </header>

      <div className={`${floating ? "min-h-0 flex-1" : "h-72"} overflow-y-auto space-y-2 px-3 py-3`}>
        {messages.length === 0 ? (
          <p className="mt-10 text-center text-xs text-sky-700">No messages yet. Send a message to start the conversation.</p>
        ) : (
          messages.map(m => {
            const mine = m.sender_role === ownRole;
            if (m.is_deleted) {
              return (
                <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className="max-w-[85%] rounded-2xl border border-dashed border-sky-300 px-3 py-1.5 text-[11px] italic text-sky-700">
                    {mine ? "You unsent a message" : `${otherLabel} unsent a message`}
                  </div>
                </div>
              );
            }
            return (
              <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs whitespace-pre-wrap shadow-sm ${
                  mine ? "rounded-br-sm bg-sky-600 text-white" : "rounded-bl-sm bg-white border border-sky-200 text-gray-800"
                }`}>
                  <p className={`text-[10px] font-semibold mb-0.5 ${mine ? "text-sky-100" : "text-sky-700"}`}>
                    {mine ? "You" : otherLabel}
                  </p>
                  {m.body}
                  <MessageAttachments attachments={m.attachments ?? []} />
                  <p className={`mt-1 text-[10px] ${mine ? "text-sky-100" : "text-gray-400"}`}>
                    {formatTime(m.created_at)}
                    {mine && onUnsend && (
                      <button
                        type="button"
                        onClick={() => { void onUnsend(m.message_id); }}
                        className="ml-2 underline decoration-dotted hover:text-white"
                      >
                        Unsend
                      </button>
                    )}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <div className="border-t-2 border-sky-200 bg-white/70 px-3 pb-3 pt-1">
        <ReplyComposer placeholder={placeholder} requestUploadUrls={requestUploadUrls} onSend={onSend} onSent={onSent} />
      </div>
    </section>
  );
};
